import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { signOut } from "firebase/auth";
import { auth, updateUserDatabase } from "shared/utils/firebase";
import { getOrAssignSiliconId } from "shared/utils/siliconId";
import { dispatchWelcomeEmail } from "shared/utils/emailService";
import { saveSessionToCookie } from "shared/utils/sessionBridge";
import {
  isPhoneTaken,
  isEmailTaken,
  validateEmailForRole,
  isFreeEmailProvider,
} from "shared/utils/userValidation";
import styles from "./OnboardingModal.module.css";

// ── Lists & Databases ────────────────────────────────────────────────────────

const ALL_INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Chandigarh", "Puducherry"
];

const POPULAR_STREAMS = [
  "VLSI Design & Microelectronics",
  "Electronics & Communication Engineering (ECE)",
  "Electrical & Electronics Engineering (EEE)",
  "Embedded Systems & IoT",
  "Computer Science & Engineering (CSE)",
  "Information Technology (IT)",
  "Artificial Intelligence & Machine Learning (AI/ML)",
  "Instrumentation & Control Engineering (ICE)",
  "Mechanical Engineering (ME)",
  "Other Engineering / Science Stream"
];

const POPULAR_COMPANIES = [
  "Intel Corporation",
  "Qualcomm",
  "NVIDIA",
  "Texas Instruments",
  "Synopsys",
  "Cadence Design Systems",
  "Arm",
  "NXP Semiconductors",
  "STMicroelectronics",
  "Broadcom",
  "MediaTek",
  "Samsung Semiconductor",
  "Micron Technology",
  "AMD",
  "Tata Consultancy Services (TCS)",
  "Infosys",
  "Wipro",
  "L&T Semiconductor Technologies",
  "eInfochips",
  "Tessolve Semiconductor",
  "Other Organization"
];



export default function OnboardingModal({
  isOpen,
  userDetails = {},
  onSuccess = () => {},
  onCancel = null
}) {
  const uid = userDetails?.uid || (auth.currentUser ? auth.currentUser.uid : null);
  const provider = userDetails?.provider || "social";
  const isPhoneProvider = provider === "phone" || Boolean(userDetails?.phoneVerified);
  const isGoogleProvider = provider === "google";

  // Pre-fill initial name (skip temporary placeholders like "User 1234")
  const initialName = (() => {
    const raw = userDetails?.name || userDetails?.displayName || "";
    if (raw.startsWith("User ") && raw.length <= 10) return "";
    return raw;
  })();

  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(userDetails?.email || "");
  const [phone, setPhone] = useState(userDetails?.phone || userDetails?.phoneNumber || "");
  const [role, setRole] = useState(userDetails?.role && userDetails.role !== "Admin" ? userDetails.role : "Student");
  const [stream, setStream] = useState(userDetails?.stream || POPULAR_STREAMS[0]);
  const [customStream, setCustomStream] = useState("");
  const [institute, setInstitute] = useState(userDetails?.institute || "");
  const [company, setCompany] = useState(userDetails?.company || POPULAR_COMPANIES[0]);
  const [customCompany, setCustomCompany] = useState("");
  const [designation, setDesignation] = useState(userDetails?.designation || "");
  const [state, setState] = useState(userDetails?.state || "Karnataka");
  const [city, setCity] = useState(userDetails?.city || "");

  const [siliconId, setSiliconId] = useState(userDetails?.siliconId || "");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [emailValidationStatus, setEmailValidationStatus] = useState({ valid: true, message: "" });
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const emailCheckTimeoutRef = useRef(null);

  const [phoneValidationStatus, setPhoneValidationStatus] = useState({ valid: true, message: "" });
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const phoneCheckTimeoutRef = useRef(null);

  // Lock body scroll while modal is active
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Sync state if userDetails changes
  useEffect(() => {
    if (userDetails) {
      if (!name) {
        const raw = userDetails.name || userDetails.displayName || "";
        if (!raw.startsWith("User ")) setName(raw);
      }
      if (!email && userDetails.email) setEmail(userDetails.email);
      if (!phone && (userDetails.phone || userDetails.phoneNumber)) {
        setPhone(userDetails.phone || userDetails.phoneNumber);
      }
      if (userDetails.role && userDetails.role !== "Admin") {
        setRole(userDetails.role);
      }
      if (userDetails.siliconId) setSiliconId(userDetails.siliconId);
    }
  }, [userDetails]);

  // Ensure user has their permanent Silicon ID or candidate generated for display
  useEffect(() => {
    let isMounted = true;
    if (isOpen && !siliconId && uid) {
      getOrAssignSiliconId(uid, userDetails).then((assigned) => {
        if (isMounted && assigned) setSiliconId(assigned);
      }).catch(() => {});
    }
    return () => { isMounted = false; };
  }, [isOpen, siliconId, uid, userDetails]);

  if (!isOpen) return null;

  // ── Real-Time Validation Handlers ──────────────────────────────────────────

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    // If switching back to Student and original Google email was provided, restore it
    if (newRole === "Student" && userDetails?.email && isFreeEmailProvider(userDetails.email)) {
      setEmail(userDetails.email);
      handleEmailChange(userDetails.email, newRole);
    } else if (email) {
      handleEmailChange(email, newRole);
    }
  };

  const handlePhoneChange = (val) => {
    setPhone(val);
    if (phoneCheckTimeoutRef.current) clearTimeout(phoneCheckTimeoutRef.current);

    const clean = val.replace(/\D/g, "");
    if (!clean) {
      setPhoneValidationStatus({ valid: false, message: "Mobile number is required*" });
      return;
    }
    if (clean.length < 10) {
      setPhoneValidationStatus({
        valid: false,
        message: "Please enter at least 10 digits",
      });
      return;
    }

    setIsCheckingPhone(true);
    phoneCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const taken = await isPhoneTaken(val, uid);
        setIsCheckingPhone(false);
        if (taken) {
          setPhoneValidationStatus({
            valid: false,
            message: "⚠️ This mobile number is already registered to another account.",
            isDuplicate: true,
          });
        } else {
          setPhoneValidationStatus({
            valid: true,
            message: "✓ Mobile number is available",
            isDuplicate: false,
          });
        }
      } catch (e) {
        setIsCheckingPhone(false);
      }
    }, 350);
  };

  const handleEmailChange = (val, targetRole = role) => {
    setEmail(val);
    if (emailCheckTimeoutRef.current) clearTimeout(emailCheckTimeoutRef.current);

    if (!val || !val.trim()) {
      setEmailValidationStatus({ valid: false, message: "Email address is required*" });
      return;
    }

    const roleValidation = validateEmailForRole(val, targetRole);
    if (!roleValidation.valid) {
      setEmailValidationStatus(roleValidation);
      return;
    }

    setIsCheckingEmail(true);
    setEmailValidationStatus({
      valid: true,
      message: "Checking availability...",
      isEducational: roleValidation.isEducational,
    });

    emailCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const taken = await isEmailTaken(val, uid);
        setIsCheckingEmail(false);
        if (taken) {
          setEmailValidationStatus({
            valid: false,
            message: "⚠️ This email is already registered to another account.",
            isDuplicate: true,
            isEducational: false,
          });
        } else {
          setEmailValidationStatus({
            valid: true,
            message: roleValidation.isEducational
              ? `🎓 Educational email verified and available for ${targetRole}`
              : `✓ Email is available and valid for ${targetRole}`,
            isDuplicate: false,
            isEducational: roleValidation.isEducational,
          });
        }
      } catch (e) {
        setIsCheckingEmail(false);
      }
    }, 350);
  };

  // ── Validation logic ────────────────────────────────────────────────────────

  const validate = () => {
    if (!role) {
      return "Please select your account role (Student, Professional, or Organisation).";
    }

    if (!name || name.trim().length < 2) {
      return "Please enter your full name (minimum 2 characters).";
    }

    if (!email || !email.includes("@")) {
      return "Please enter a valid email address.";
    }

    const emailRoleValidation = validateEmailForRole(email, role);
    if (!emailRoleValidation.valid) {
      return emailRoleValidation.message;
    }

    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      return "Please enter a valid 10-digit mobile phone number.";
    }

    if (role === "Student") {
      const activeStream = stream === "Other Engineering / Science Stream" ? customStream : stream;
      if (!activeStream || !activeStream.trim()) {
        return "Please specify your degree or academic stream.";
      }
      if (!institute || !institute.trim()) {
        return "Please provide your college or university name.";
      }
    } else if (role === "Professional") {
      const activeCompany = company === "Other Organization" ? customCompany : company;
      if (!activeCompany || !activeCompany.trim()) {
        return "Please specify your company or organization name.";
      }
      if (!designation || !designation.trim()) {
        return "Please provide your designation or job title.";
      }
    } else if (role === "Organisation") {
      if (!institute || !institute.trim()) {
        return "Please provide your organization / institution name.";
      }
      if (!designation || !designation.trim()) {
        return "Please specify your department or role in the organization.";
      }
    }

    return "";
  };

  // ── Handle Submit ──────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const targetUid = uid || auth.currentUser?.uid;
      if (!targetUid) {
        throw new Error("User session not found. Please log in again.");
      }

      // 1. Ensure permanent Silicon ID
      let finalSiliconId = siliconId || userDetails?.siliconId;
      if (!finalSiliconId) {
        finalSiliconId = await getOrAssignSiliconId(targetUid, userDetails);
        setSiliconId(finalSiliconId);
      }

      const activeStream = stream === "Other Engineering / Science Stream" ? customStream.trim() : stream;
      const activeCompany = company === "Other Organization" ? customCompany.trim() : company;

      // 2. Prepare payload
      const cleanPhone = phone.trim();
      const cleanEmail = email.trim().toLowerCase();
      const cleanName = name.trim();

      // Check duplicate phone against other users
      const phoneTaken = await isPhoneTaken(cleanPhone, targetUid);
      if (phoneTaken) {
        setErrorMsg("⚠️ This mobile phone number is already registered to another account. Please provide your own unique mobile number.");
        setLoading(false);
        return;
      }

      // Check role email validation
      const roleCheck = validateEmailForRole(cleanEmail, role);
      if (!roleCheck.valid) {
        setErrorMsg(roleCheck.message);
        setLoading(false);
        return;
      }

      // Check duplicate email against other users
      const emailTaken = await isEmailTaken(cleanEmail, targetUid);
      if (emailTaken) {
        setErrorMsg("⚠️ This email address is already registered to another account. Please provide your own unique email address.");
        setLoading(false);
        return;
      }

      const profilePayload = {
        name: cleanName,
        displayName: cleanName,
        email: cleanEmail,
        phone: cleanPhone,
        role: role,
        siliconId: finalSiliconId,
        state: state.trim(),
        city: city.trim(),
        onboardingCompleted: true,
        isProfileComplete: true,
        updatedAt: new Date()
      };

      if (role === "Student") {
        profilePayload.stream = activeStream;
        profilePayload.institute = institute.trim();
      } else if (role === "Professional") {
        profilePayload.company = activeCompany;
        profilePayload.designation = designation.trim();
      } else if (role === "Organisation") {
        profilePayload.institute = institute.trim();
        profilePayload.company = institute.trim();
        profilePayload.designation = designation.trim();
      }

      if (isPhoneProvider) {
        profilePayload.phoneVerified = true;
      }

      // 3. Write to Firestore
      await updateUserDatabase(profilePayload, targetUid);

      const mergedProfile = {
        ...(userDetails || {}),
        ...profilePayload,
        uid: targetUid
      };

      // 4. Dispatch Welcome Email if not yet sent
      if (!mergedProfile.welcomeEmailSent && cleanEmail) {
        dispatchWelcomeEmail({
          uid: targetUid,
          email: cleanEmail,
          name: cleanName,
          siliconId: finalSiliconId,
          role: role
        }).catch((err) => console.warn("[Onboarding] Welcome email warning:", err));
      }

      // 5. Save cross-subdomain SSO bridge
      try {
        saveSessionToCookie({
          ...(auth.currentUser || {}),
          ...mergedProfile,
          toJSON: () => (auth.currentUser ? auth.currentUser.toJSON() : {})
        });
      } catch (bridgeErr) {
        console.warn("[Onboarding] Session bridge sync:", bridgeErr);
      }

      // 6. Broadcast profile update to all components
      window.dispatchEvent(
        new CustomEvent("slx_user_profile_updated", { detail: mergedProfile })
      );

      setLoading(false);
      onSuccess(mergedProfile);
    } catch (err) {
      console.error("[Onboarding] Error submitting profile:", err);
      setErrorMsg(err.message || "Failed to update profile. Please try again.");
      setLoading(false);
    }
  };

  // ── Handle Sign Out (Safe Exit) ────────────────────────────────────────────

  const handleSignOut = async () => {
    try {
      if (onCancel) {
        onCancel();
      } else {
        await signOut(auth);
        window.location.href = "/";
      }
    } catch (err) {
      console.error("[Onboarding] Error during signout:", err);
    }
  };

  const modalContent = (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalCard}>
        {/* Header & Silicon ID Badge */}
        <div className={styles.header}>
          <div className={styles.badgeRow}>
            <span className={styles.compulsoryBadge}>
              <span className={styles.pulsingDot} />
              Compulsory Onboarding
            </span>
            {siliconId && (
              <span className={styles.siliconIdBadge} title="Permanent Silicon ID">
                ⚡ {siliconId}
              </span>
            )}
          </div>
          <h2 className={styles.title}>Complete Your SiliconLogix Profile</h2>
          <p className={styles.subtitle}>
            {provider === "email"
              ? "Welcome to SiliconLogix! Please complete your academic/professional credentials and contact details to finalize your account setup."
              : `Welcome! Because you authenticated via ${provider.toUpperCase()}, please complete these required details to activate your student/engineer account.`}
          </p>
        </div>

        {errorMsg && (
          <div className={styles.errorAlert} role="alert">
            <span>⚠️</span>
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Section 1: Account Role - Compact design matching signup page */}
          <div className={styles.section} style={{ marginBottom: "1.25rem" }}>
            <h3 className={styles.sectionTitle} style={{ marginBottom: "0.5rem" }}>
              1. Account Role <span className={styles.required}>*</span>
            </h3>
            <div className={styles.rolesContainer}>
              {[
                { id: "Student", label: "Student", icon: "🎓" },
                { id: "Professional", label: "Professional", icon: "💼" },
                { id: "Organisation", label: "Organisation", icon: "🏛️" },
              ].map((r) => (
                <label
                  key={r.id}
                  className={`${styles.radioLabel} ${role === r.id ? styles.activeRadio : ""}`}
                >
                  <input
                    type="radio"
                    name="onboarding-role"
                    value={r.id}
                    checked={role === r.id}
                    onChange={() => handleRoleChange(r.id)}
                  />
                  <span>{r.icon}</span>
                  <span>{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Section 2: Personal & Contact Information */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>2. Personal & Contact Information</h3>

            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="onboarding-name">
                  Full Name <span className={styles.required}>*</span>
                </label>
                <input
                  id="onboarding-name"
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={styles.input}
                  autoComplete="name"
                />
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="onboarding-email">
                  Email Address <span className={styles.required}>*</span>
                  {isCheckingEmail && (
                    <span className={styles.statusSpinner}>⏳</span>
                  )}
                  {!isCheckingEmail && email && (
                    <span className={styles.statusIndicator}>
                      {emailValidationStatus.valid
                        ? emailValidationStatus.isEducational
                          ? "🎓"
                          : "✓"
                        : "✗"}
                    </span>
                  )}
                  {isGoogleProvider && role === "Student" && (
                    <span className={styles.verifiedTag}>✓ Google Verified</span>
                  )}
                  {!isGoogleProvider && Boolean(userDetails?.email) && (
                    <span className={styles.verifiedTag}>✓ Verified</span>
                  )}
                </label>
                <input
                  id="onboarding-email"
                  type="email"
                  required
                  placeholder={
                    role === "Student"
                      ? "e.g. name@gmail.com"
                      : role === "Professional"
                        ? "e.g. name@company.com"
                        : "e.g. contact@institution.edu"
                  }
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  disabled={Boolean(userDetails?.email) && (role === "Student" || !isFreeEmailProvider(userDetails?.email))}
                  className={`${styles.input} ${
                    Boolean(userDetails?.email) && (role === "Student" || !isFreeEmailProvider(userDetails?.email))
                      ? styles.readOnlyInput
                      : ""
                  }`}
                  style={{
                    borderColor:
                      email && !emailValidationStatus.valid ? "#ef4444" : undefined,
                  }}
                  autoComplete="email"
                />
                {Boolean(userDetails?.email) && role === "Professional" && isFreeEmailProvider(userDetails?.email) && (
                  <small style={{ color: "#3b82f6", display: "block", marginTop: "4px", fontSize: "0.8rem" }}>
                    ℹ️ Your Google account is personal ({userDetails.email}). Please provide your corporate/business email for Professional access.
                  </small>
                )}
                {email && (
                  <small
                    style={{
                      display: "block",
                      marginTop: "4px",
                      fontSize: "0.8rem",
                      color: emailValidationStatus.valid ? "#10b981" : "#ef4444",
                    }}
                  >
                    {emailValidationStatus.message}
                  </small>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="onboarding-phone">
                  Mobile Number <span className={styles.required}>*</span>
                  {isCheckingPhone && (
                    <span className={styles.statusSpinner}>⏳</span>
                  )}
                  {!isCheckingPhone && phone && (
                    <span className={styles.statusIndicator}>
                      {phoneValidationStatus.valid ? "✓" : "✗"}
                    </span>
                  )}
                  {isPhoneProvider && (
                    <span className={styles.verifiedTag}>✓ OTP Verified</span>
                  )}
                </label>
                <input
                  id="onboarding-phone"
                  type="tel"
                  required
                  placeholder="e.g. +91 98765 43210"
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  disabled={isPhoneProvider && Boolean(userDetails?.phone)}
                  className={`${styles.input} ${isPhoneProvider && userDetails?.phone ? styles.readOnlyInput : ""}`}
                  style={{
                    borderColor:
                      phone && !phoneValidationStatus.valid ? "#ef4444" : undefined,
                  }}
                  autoComplete="tel"
                />
                {phone && (
                  <small
                    style={{
                      display: "block",
                      marginTop: "4px",
                      fontSize: "0.8rem",
                      color: phoneValidationStatus.valid ? "#10b981" : "#ef4444",
                    }}
                  >
                    {phoneValidationStatus.message}
                  </small>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="onboarding-state">State / UT</label>
                <select
                  id="onboarding-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className={styles.select}
                >
                  {ALL_INDIAN_STATES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.inputGroup}>
                <label htmlFor="onboarding-city">City</label>
                <input
                  id="onboarding-city"
                  type="text"
                  placeholder="e.g. Bengaluru, Hyderabad, Pune"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className={styles.input}
                  autoComplete="address-level2"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Role Specific Credentials */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              3. {role === "Student" ? "Academic Credentials" : "Professional Details"}
            </h3>

            {role === "Student" && (
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label htmlFor="onboarding-stream">
                    Branch / Graduation Stream <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="onboarding-stream"
                    value={stream}
                    onChange={(e) => setStream(e.target.value)}
                    className={styles.select}
                  >
                    {POPULAR_STREAMS.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                  {stream === "Other Engineering / Science Stream" && (
                    <input
                      type="text"
                      placeholder="Specify your stream (e.g. Mechatronics, Physics)"
                      value={customStream}
                      onChange={(e) => setCustomStream(e.target.value)}
                      className={`${styles.input} ${styles.nestedInput}`}
                      required
                    />
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="onboarding-college">
                    College / University Name <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="onboarding-college"
                    type="text"
                    required
                    placeholder="e.g. RV College of Engineering, Bangalore"
                    value={institute}
                    onChange={(e) => setInstitute(e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>
            )}

            {role === "Professional" && (
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label htmlFor="onboarding-company">
                    Company / Employer <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="onboarding-company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className={styles.select}
                  >
                    {POPULAR_COMPANIES.map((cmp) => (
                      <option key={cmp} value={cmp}>
                        {cmp}
                      </option>
                    ))}
                  </select>
                  {company === "Other Organization" && (
                    <input
                      type="text"
                      placeholder="Enter company / organization name"
                      value={customCompany}
                      onChange={(e) => setCustomCompany(e.target.value)}
                      className={`${styles.input} ${styles.nestedInput}`}
                      required
                    />
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="onboarding-designation">
                    Job Title / Designation <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="onboarding-designation"
                    type="text"
                    required
                    placeholder="e.g. RTL Design Engineer, Verification Lead"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>
            )}

            {role === "Organisation" && (
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label htmlFor="onboarding-org">
                    Institution / University Name <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="onboarding-org"
                    type="text"
                    required
                    placeholder="e.g. National Institute of Technology"
                    value={institute}
                    onChange={(e) => setInstitute(e.target.value)}
                    className={styles.input}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="onboarding-dept">
                    Department / Your Role <span className={styles.required}>*</span>
                  </label>
                  <input
                    id="onboarding-dept"
                    type="text"
                    required
                    placeholder="e.g. Head of ECE Dept, Lab In-charge"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className={styles.actions}>
            <button
              type="submit"
              disabled={loading}
              className={styles.submitBtn}
            >
              {loading ? (
                <>
                  <span className={styles.spinner} />
                  Saving Profile...
                </>
              ) : (
                "Complete Profile & Continue →"
              )}
            </button>

            <button
              type="button"
              onClick={handleSignOut}
              className={styles.cancelBtn}
            >
              Cancel & Sign Out
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
